import {debounceTime, Options, TableDataRequest} from '@fl/cmsch-fe-library';
import {debounce, isEmpty, isNumber} from 'lodash/fp';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {none, Opt, opt} from 'ts-opt';

import {ColumnHeader} from 'api/gen/ColumnHeader';
import {OrganizationRoleManagement} from 'api/gen/OrganizationRoleManagement';
import {UserProfileOrganizationRoleFarmsCenters} from 'api/gen/UserProfileOrganizationRoleFarmsCenters';
import {ManagementOrganizationFormValues} from 'app/admin/types/management-organization-form-values';
import {genFarmsAndCentersTooltip} from 'app/admin/utils/gen-farms-and-centers-tooltip';
import {managementOrganizationsValidate} from 'app/admin/utils/management-organization-validate';
import {TFunction, useOurTranslation} from 'app/translations';
import {AsyncValidationSource} from 'utils/async-validation-source';
import {Errors} from 'utils/validator';

import {managementOrganizationsRowMapper} from '../components/management-organization-table/row-mapper';
import {adminAction} from '../model';
import {
    managementOrganizationCenterOptionsTooltipSelector,
    managementOrganizationFarmOptionsTooltipSelector,
    managementOrganizationsRoleOptionsSelector,
    managementOrganizationsUserOptionsSelector,
    simpleAdminSelector,
} from '../model/selector';
import {CreateOrEditOrNull} from '../types/create-or-edit-or-null';
import {ManagementOrganizationsTableType} from '../types/management-organizations';
import {defaultUserProfileAndRoleIds, UserProfileAndRole} from '../types/user-profile-and-role-ids';

const organizationForm = 'organizationForm';

interface UseManagementOrganizationSetup {
    organizationId: Opt<number>;
    organizationsOptions: Options<number>;
    managementOrganizationsExtraColumns: Array<ColumnHeader>;
    roleOptions: Options<number>;
    userProfileOptions: Options<number>;
    userProfileAndRoles: UserProfileAndRole;
    visibleModal: boolean;
    modalTitle: string;
    organizationDetail: Opt<UserProfileOrganizationRoleFarmsCenters>;
    isEdit: boolean;
    t: TFunction<'admin'>;
    tCommon: TFunction<'common'>;
    detailHasAccessToAllFarms: boolean;
    detailHasAccessToAllCenters: boolean;
    searchManagementOrganizations(text: string): void;
    handleManagementOrganizationSelectChangeValue(value: Opt<number>): void;
    onManagementOrganizationsTableFetch(
        tableDataRequest: Opt<TableDataRequest<ManagementOrganizationsTableType>>
    ): void;
    removeRolesForUser(_id: number, user: ManagementOrganizationsTableType): void;
    removeUsersFromRole(columnRoleId: string): void;
    removeConfirmTitle(_id: number, user: ManagementOrganizationsTableType): string;
    handleColumnOpenCreateModal(roleId: string, roleName: string): void;
    handleRowOpenCreateModal(_id: number, record: ManagementOrganizationsTableType): void;
    handleOpenCreateModalWithoutLimitations(): void;
    handleSubmitModal(): void;
    handleUserSearch(searchText: string): void;
    handleRoleSearch(searchText: string): void;
    handleCloseModal(): void;
    validateDebounced(source: AsyncValidationSource): void;
    validate(values: ManagementOrganizationFormValues): Errors<ManagementOrganizationFormValues>;
    rowMapper(row: ManagementOrganizationsTableType): ManagementOrganizationsTableType;
    clearOrganizationDetail(): void;
}

/** Builds the state and callbacks used by the organization management screen. */
// eslint-disable-next-line max-lines-per-function
export const useManagementOrganizationSetup = (): UseManagementOrganizationSetup => {
    const dispatch = useDispatch();
    const {t, tCommon} = useOurTranslation('admin');

    const [organizationId, setOrganizationId] = useState<Opt<number>>(opt(undefined));
    const [userProfileAndRoles, setUserProfileAndRoleIds] = useState<UserProfileAndRole>(defaultUserProfileAndRoleIds);
    const [actionMode, setActionMode] = useState<CreateOrEditOrNull>(null);
    const [detailHasAccessToAllFarms, setDetailHasAccessToAllFarms] = useState(false);
    const [detailHasAccessToAllCenters, setDetailHasAccessToAllCenters] = useState(false);

    const organizationsOptions = useSelector(simpleAdminSelector.managementOrganizationsOptions);
    const subjectManagementModalVisible = useSelector(simpleAdminSelector.subjectManagementModalVisible);
    const organizationDetail = useSelector(simpleAdminSelector.managementOrganizationDetail);
    const roleOptions = useSelector(managementOrganizationsRoleOptionsSelector);
    const userProfileOptions = useSelector(managementOrganizationsUserOptionsSelector);
    const managementOrganizationsExtraColumns = useSelector(simpleAdminSelector.managementOrganizationsExtraColumns);
    const managementOrganizationsFarmTooltipOptions = useSelector(managementOrganizationFarmOptionsTooltipSelector);
    const managementOrganizationsCenterTooltipOptions = useSelector(managementOrganizationCenterOptionsTooltipSelector);

    useEffect(() => {
        const orgId = organizationId.orUndef();
        if (orgId) {
            dispatch(adminAction.getManagementOrganizationFarms(orgId));
            dispatch(adminAction.getManagementOrganizationCenters(orgId));
        }
    }, [dispatch, organizationId]);

    useEffect(() => {
        if (!isEmpty(organizationsOptions) && organizationsOptions.length === 1) {
            const firstOption = opt(organizationsOptions).headIn().prop('value');
            setOrganizationId(firstOption);
            dispatch(adminAction.getManagementOrganizationsTableData(opt({action: 'reset'}), firstOption.orUndef()));
        }
    }, [organizationsOptions, dispatch]);

    const isEdit = useMemo(() => actionMode === 'edit', [actionMode]);
    const visibleModal = useMemo(() =>
        subjectManagementModalVisible === organizationForm && (!isEdit || organizationDetail.isFull),
    [subjectManagementModalVisible, organizationDetail, isEdit]);
    const organizationName = useMemo(() =>
        organizationId.isFull ? organizationsOptions.find(x => x.value === organizationId.orUndef())?.label : '',
    [organizationId, organizationsOptions]);
    const modalTitle = useMemo(() => t(isEdit
        ? 'updateAccessToOrganization'
        : 'addAccessToOrganization',
    {organization: organizationName}),
    [t, organizationName, isEdit]);

    const validateData = useCallback((source: AsyncValidationSource) => {
        dispatch(adminAction.validateManagementOrganization(source));
    }, [dispatch]);
    const validateDebounced = useMemo(() => debounce(debounceTime)(validateData), [validateData]);

    const searchManagementOrganizations = useCallback((text: string) => {
        dispatch(adminAction.getManagementOrganizationsOptions(text));
    }, [dispatch]);

    const handleManagementOrganizationSelectChangeValue = useCallback((value: Opt<number>) => {
        dispatch(adminAction.getManagementOrganizationsTableData(opt({action: 'reset'}), value.orUndef()));
        setOrganizationId(value);
    }, [dispatch]);

    const onManagementOrganizationsTableFetch = useCallback((
        tableDataRequest: Opt<TableDataRequest<ManagementOrganizationsTableType>>,
    ) => {
        if (tableDataRequest.isFull) {
            dispatch(adminAction.getManagementOrganizationsTableData(tableDataRequest, organizationId.orUndef()));
        }
    }, [organizationId, dispatch]);

    const removeRolesForUser = useCallback((_id: number, user: ManagementOrganizationsTableType) => {
        if (organizationId.isFull) {
            dispatch(adminAction.removeOrganizationRolesForUser(
                user.userProfileId,
                organizationId.orCrash('Organization id not found for removing user roles'),
            ));
        }
    }, [dispatch, organizationId]);

    const clearOrganizationDetail = useCallback(() => {
        dispatch(adminAction.getManagementOrganizationDetailSuccess(none));
    }, [dispatch]);

    const removeUsersFromRole = useCallback((columnRoleId: string) => {
        const roleIdNarrowed = opt(columnRoleId).map(Number).narrow(isNumber);
        if (organizationId.isFull && roleIdNarrowed.isFull) {
            dispatch(adminAction.removeUsersFromOrganizationRole(
                roleIdNarrowed.orCrash('Role ID is not a number'),
                organizationId.orCrash('Organization ID not found for removing user roles'),
            ));
        }
    }, [dispatch, organizationId]);

    const removeConfirmTitle = useCallback((_id: number, user: ManagementOrganizationsTableType) =>
        t('confirmRemovingUserRoles', {user: user.username})
    , [t]);

    const handleSubmitModal = useCallback(() => {
        if (organizationId.isFull) {
            const submitAction = isEdit
                ? adminAction.updateManagementOrganizationDetail
                : adminAction.createManagementOrganization;
            const safeOrganizationId = organizationId.orCrash('Organization ID not found');
            dispatch(submitAction(safeOrganizationId));
        }
    }, [isEdit, organizationId, dispatch]);

    const handleOpenCreateModal = useCallback(() => {
        setActionMode('create');
        dispatch(adminAction.setManagementSubjectModalVisible(organizationForm));
    }, [dispatch]);

    const handleOpenEditModal = useCallback(() => {
        setActionMode('edit');
        dispatch(adminAction.setManagementSubjectModalVisible(organizationForm));
    }, [dispatch]);

    const handleDynamicCellClick = useCallback((role: OrganizationRoleManagement) => {
        dispatch(adminAction.getManagementOrganizationDetail(role.userProfileOrganizationRoleId));
        setDetailHasAccessToAllCenters(role.hasAccessToAllOrganizationCenters);
        setDetailHasAccessToAllFarms(role.hasAccessToAllFarms);
        handleOpenEditModal();
    }, [dispatch, handleOpenEditModal]);

    const computeFarmsAndCentersTooltip = useCallback((
        farmIds: Array<number> | null,
        centerIds: Array<number> | null,
        hasAccessToAllOrganizationCenters: boolean,
        hasAccessToAllFarms: boolean,
    ) => genFarmsAndCentersTooltip({
        farmIds,
        hasAccessToAllOrganizationCenters,
        managementOrganizationsCenterTooltipOptions,
        managementOrganizationsFarmTooltipOptions,
        hasAccessToAllFarms,
        centerIds,
        t,
    }), [managementOrganizationsCenterTooltipOptions, managementOrganizationsFarmTooltipOptions, t]);

    const rowMapper = useCallback((row: ManagementOrganizationsTableType) =>
        managementOrganizationsRowMapper(row, computeFarmsAndCentersTooltip, handleDynamicCellClick),
    [computeFarmsAndCentersTooltip, handleDynamicCellClick]);

    const handleColumnOpenCreateModal = useCallback((columnRoleId: string, roleName: string) => {
        const roleIdNarrowed = opt(columnRoleId).map(Number).narrow(isNumber);

        if (roleIdNarrowed.isFull) {
            setUserProfileAndRoleIds({
                role: {
                    id: roleIdNarrowed.orCrash('Open create modal in other subjects: Role ID is not a number'),
                    name: roleName,
                },
                userProfile: null,
            });
            handleOpenCreateModal();
        }
    }, [handleOpenCreateModal]);

    const handleRowOpenCreateModal = useCallback((_id: number, record: ManagementOrganizationsTableType) => {
        setUserProfileAndRoleIds({
            role: null,
            userProfile: {
                id: record.userProfileId,
                name: record.username,
            },
        });
        handleOpenCreateModal();
    }, [handleOpenCreateModal]);

    const handleOpenCreateModalWithoutLimitations = useCallback(() => {
        setUserProfileAndRoleIds(defaultUserProfileAndRoleIds);
        handleOpenCreateModal();
    }, [handleOpenCreateModal]);

    const handleCloseModal = useCallback(() => {
        setActionMode(null);
        dispatch(adminAction.setManagementSubjectModalVisible(null));
        dispatch(adminAction.setManagementOrganizationsUsers([]));
        dispatch(adminAction.setManagementOrganizationsRoles([]));
    }, [dispatch]);

    const handleUserSearch = useCallback((searchText: string): void => {
        if (organizationId.isFull) {
            dispatch(adminAction.searchManagementOrganizationsUsers(
                searchText,
                organizationId.orCrash('User search in other subjects: Unknown other subject ID'),
            ));
        }
    }, [dispatch, organizationId]);

    const handleRoleSearch = useCallback((searchText: string): void => {
        dispatch(adminAction.searchManagementOrganizationsRoles(searchText));
    }, [dispatch]);

    return {
        organizationId,
        organizationsOptions,
        managementOrganizationsExtraColumns,
        roleOptions,
        userProfileAndRoles,
        userProfileOptions,
        visibleModal,
        modalTitle,
        organizationDetail,
        isEdit,
        t,
        tCommon,
        searchManagementOrganizations,
        handleManagementOrganizationSelectChangeValue,
        onManagementOrganizationsTableFetch,
        removeConfirmTitle,
        removeRolesForUser,
        removeUsersFromRole,
        handleCloseModal,
        handleColumnOpenCreateModal,
        handleOpenCreateModalWithoutLimitations,
        handleRoleSearch,
        handleRowOpenCreateModal,
        handleSubmitModal,
        handleUserSearch,
        detailHasAccessToAllFarms,
        detailHasAccessToAllCenters,
        validate: managementOrganizationsValidate,
        rowMapper,
        clearOrganizationDetail,
        validateDebounced,
    };
};
